import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Link2Icon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TrackerDataType } from '../../shared/constants/constants';
import {
  adderHeaderTitleSx,
  queryAdderPopupActionButtonSx,
  queryAdderPopupFieldLabelSx,
  queryAdderPopupFormSpacing,
  queryAdderPopupInputSx,
  utilityCancelButtonSx,
  utilityModalContentSx,
  utilityModalDialogSx,
  utilityModalFooterSx,
  utilityModalHeaderSx,
  utilityModalIconTileSx,
  utilityModalPaperSx,
  utilityModalTitleSx,
  utilityPrimaryActionButtonSx,
} from './queryAdderPopupStyles';

type LiveLinkDraft = {
  type?: string;
  name?: string;
  description?: string;
  path?: string;
  url?: string;
};

interface LiveLinkDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialValue?: LiveLinkDraft | null;
  onSubmit: (value: { type: 'url'; name: string; description: string; path: string }) => void;
  onDelete: () => void;
  type?: TrackerDataType;
}

const defaultLiveLinkValue = {
  url: '',
  name: '',
  description: '',
};

function LiveLinkDialog({ open, setOpen, initialValue, onSubmit, onDelete, type }: Readonly<LiveLinkDialogProps>) {
  const [dialogValue, setDialogValue] = useState(defaultLiveLinkValue);
  const isStoryLink = type === TrackerDataType.MEDIA_INTERACTION;

  useEffect(() => {
    if (!open) {
      setDialogValue(defaultLiveLinkValue);
      return;
    }

    setDialogValue({
      url: initialValue?.path ?? initialValue?.url ?? '',
      name: initialValue?.name ?? '',
      description: initialValue?.description ?? '',
    });
  }, [initialValue, open]);

  const handleClose = () => {
    setDialogValue(defaultLiveLinkValue);
    setOpen(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onSubmit({
      type: 'url',
      name: dialogValue.name,
      description: dialogValue.description,
      path: dialogValue.url,
    });
    handleClose();
  };

  const isEditing = Boolean(initialValue);
  let dialogTitle = isStoryLink ? 'Add Story' : 'Add Live Link';
  if (isEditing) {
    dialogTitle = isStoryLink ? 'Edit Story' : 'Edit Live Link';
  }
  const urlLabel = isStoryLink ? 'URL' : 'Live Link';
  const nameLabel = isStoryLink ? 'Title' : 'Link Name';
  const modalFieldLabelSx = {
    ...queryAdderPopupFieldLabelSx,
    fontWeight: 500,
  } as const;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={utilityModalDialogSx}
      PaperProps={{
        sx: {
          ...utilityModalPaperSx,
        },
      }}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        <DialogTitle sx={utilityModalHeaderSx}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={utilityModalIconTileSx}>
              <Link2Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  ...utilityModalTitleSx,
                  ...adderHeaderTitleSx,
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                }}
              >
                {dialogTitle}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ ...utilityModalContentSx, pt: '24px !important' }}>
          <Stack spacing={queryAdderPopupFormSpacing}>
            <Box>
              <Typography sx={modalFieldLabelSx}>
                {urlLabel}{' '}
                <Box component="span" sx={{ color: 'primary.main' }}>
                  *
                </Box>
              </Typography>
              <TextField
                required
                autoFocus
                fullWidth
                data-cy="live-link-url-input"
                variant="outlined"
                value={dialogValue.url}
                onChange={(event) => setDialogValue((prev) => ({ ...prev, url: event.target.value }))}
                sx={queryAdderPopupInputSx}
              />
            </Box>
            <Box>
              <Typography sx={modalFieldLabelSx}>
                {nameLabel}{' '}
                <Box component="span" sx={{ color: 'primary.main' }}>
                  *
                </Box>
              </Typography>
              <TextField
                required
                fullWidth
                data-cy="live-link-name-input"
                variant="outlined"
                value={dialogValue.name}
                onChange={(event) => setDialogValue((prev) => ({ ...prev, name: event.target.value }))}
                sx={queryAdderPopupInputSx}
              />
            </Box>
            <Box>
              <Typography sx={modalFieldLabelSx}>Description</Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                data-cy="live-link-description-input"
                variant="outlined"
                value={dialogValue.description}
                onChange={(event) => setDialogValue((prev) => ({ ...prev, description: event.target.value }))}
                sx={queryAdderPopupInputSx}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ ...utilityModalFooterSx, justifyContent: 'space-between' }}>
          <Box>
            {isEditing ? (
              <Button
                variant="outlined"
                onClick={() => {
                  onDelete();
                  handleClose();
                }}
                startIcon={<DeleteOutlineIcon />}
                sx={{
                  ...queryAdderPopupActionButtonSx,
                  color: '#DC2626',
                  borderColor: '#F3B4B4',
                  '&:hover': {
                    borderColor: '#E57373',
                    backgroundColor: '#FFF5F5',
                  },
                }}
              >
                Delete
              </Button>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={handleClose}
              startIcon={<CloseIcon />}
              variant="outlined"
              sx={{
                ...utilityCancelButtonSx,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-cy="live-link-submit-button"
              startIcon={isEditing ? <CheckIcon /> : <AddIcon />}
              variant="contained"
              sx={utilityPrimaryActionButtonSx}
            >
              {isEditing ? 'Save' : 'Add'}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default LiveLinkDialog;